import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import { metricRequestDefaults } from '../../shared/constants/constants';
import { getMetricData } from './fetchUtils';
import { flattenArray } from '../widgets/utils/dataFormatUtils';
import {
    setMetricData,
    setMetricDataLoading,
} from '../stateManagement/dashboardActions';
import { filterHasValue } from '../../shared/filtering/filtersModalUtils';

export function getMetricRequestUniqueId({ uniqueRequestString }) {
    return uniqueRequestString || uuidv4();
}

export function excludeOptOutFilters(filterValues, optOutFilters) {
    return _.pickBy(
        filterValues,
        (filter, key) =>
            filterHasValue(filter) &&
            (_.isEmpty(optOutFilters) || !optOutFilters.includes(key))
    );
}

export function excludeOptOutWhereClauses(whereClauses, optOutMeasureFilters) {
    if (_.isEmpty(optOutMeasureFilters)) {
        return whereClauses;
    }

    return whereClauses.filter(
        (whereClause) =>
            !optOutMeasureFilters.includes(whereClause.propertyName)
    );
}

export function mergeTimeAndPrecision(
    dashboardTimeAndPrecision,
    widgetTimeAndPrecision
) {
    return {
        ...dashboardTimeAndPrecision,
        ...widgetTimeAndPrecision,
    };
}

export function mergeFilterValues(
    dashboardFilterValues,
    widgetFilterValues,
    requestFilters
) {
    return {
        ...dashboardFilterValues,
        ...widgetFilterValues,
        ...requestFilters,
    };
}

function doesWhereClauseMeasureExist(collection, measure) {
    return collection.find((x) => x.propertyName === measure);
}

export function mergeWhereClausesUniqueMeasures(
    existingCollection = [],
    whereClauses = []
) {
    const updatedWhereClauses = [...existingCollection];

    whereClauses.forEach((whereClause) => {
        const existsInWidget = doesWhereClauseMeasureExist(
            existingCollection,
            whereClause.propertyName
        );

        if (!existsInWidget) {
            updatedWhereClauses.push(whereClause);
        }
    });

    return updatedWhereClauses;
}

export function mergeWhereClauses(
    dashboardWhereClauses = [],
    widgetWhereClauses = [],
    requestWhereClauses = []
) {
    return mergeWhereClausesUniqueMeasures(
        mergeWhereClausesUniqueMeasures(
            requestWhereClauses,
            widgetWhereClauses
        ),
        dashboardWhereClauses
    );
}

/* eslint-disable complexity */
export function mergeRecordLimit(
    dashboardRecordLimit = {},
    widget = {},
    baseMetricRequest = {}
) {
    const recordLimit =
        baseMetricRequest.recordLimit ||
        widget.recordLimit ||
        dashboardRecordLimit.recordLimit;

    const isDescending =
        !!baseMetricRequest.isDescending ||
        !!widget.isDescending ||
        !!dashboardRecordLimit.isDescending;

    return {
        recordLimit: parseInt(recordLimit, 10) || 0,
        isDescending,
    };
}
/* eslint-enable complexity */

export function formatFilterForBackend(filter) {
    return {
        ...filter,
        predicate: filter.predicate.valueField,
        values: filter.values.map((value) => value.valueField ?? value),
    };
}

export function formatFiltersForBackend(filters) {
    return _.mapValues(filters, formatFilterForBackend);
}

export function formatWhereClauseForBackend(whereClause) {
    return {
        ...whereClause,
        propertyName: whereClause.propertyName.valueField,
        predicate: whereClause.predicate.valueField,
    };
}

export function formatWhereClausesForBackend(whereClauses) {
    return whereClauses.map(formatWhereClauseForBackend);
}

export function applyFiltersToMetricRequest(
    baseMetricRequest,
    dashboardTimeAndPrecision,
    dashboardFilterValues,
    dashboardWhereClauses,
    dashboardRecordLimit,
    widget,
    formatForBackend
) {
    const timeAndPrecision = mergeTimeAndPrecision(
        dashboardTimeAndPrecision,
        widget.timeAndPrecision
    );

    const filters = excludeOptOutFilters(
        mergeFilterValues(
            dashboardFilterValues,
            widget.filterValues,
            baseMetricRequest.filters
        ),
        baseMetricRequest.optOutFilters
    );

    const where = excludeOptOutWhereClauses(
        mergeWhereClauses(
            dashboardWhereClauses,
            widget.whereClauses,
            baseMetricRequest.where
        ),
        baseMetricRequest.optOutMeasureFilters
    );

    const recordLimit = mergeRecordLimit(
        dashboardRecordLimit,
        widget,
        baseMetricRequest
    );

    return {
        ...baseMetricRequest,
        timeAndPrecision,
        filters: formatForBackend ? formatFiltersForBackend(filters) : filters,
        where: formatForBackend ? formatWhereClausesForBackend(where) : where,
        recordLimit: recordLimit.recordLimit,
        isDescending: !!recordLimit.isDescending,
    };
}

export function initializeWidgetMetricRequests(metricRequests) {
    if (_.isEmpty(metricRequests)) {
        return [];
    }

    return metricRequests.map((request) => {
        const baseMetricRequest = {
            ...metricRequestDefaults,
            ...request,
        };

        return {
            ...baseMetricRequest,
            uniqueRequestString: getMetricRequestUniqueId(request),
        };
    });
}

export function buildWidgetMetricRequests(
    widget,
    dashboardTimeAndPrecision,
    dashboardFilterValues,
    dashboardWhereClauses,
    dashboardRecordLimit,
    formatForBackend = true
) {
    return widget.metricRequests.map((request) => {
        const filteredMetricRequest = applyFiltersToMetricRequest(
            request,
            dashboardTimeAndPrecision,
            dashboardFilterValues,
            dashboardWhereClauses,
            dashboardRecordLimit,
            widget,
            formatForBackend
        );

        return {
            ...filteredMetricRequest,
            uniqueRequestString: getMetricRequestUniqueId(request),
        };
    });
}

export function formatLandingPageWidgetMetricRequests(widget) {
    return widget.metricRequests.map((request) => ({
        ...request,
        filters: JSON.stringify(formatFiltersForBackend(request.filters)),
        where: formatWhereClausesForBackend(request.where),
    }));
}

export function flattenAndFormatRequests(
    widgets,
    dashboardTimeAndPrecision,
    dashboardFilterValues,
    dashboardWhereClauses,
    dashboardRecordLimit
) {
    const requests = flattenArray(
        widgets
            .filter((widget) => !_.isEmpty(widget.metricRequests))
            .map((widget) =>
                buildWidgetMetricRequests(
                    widget,
                    dashboardTimeAndPrecision,
                    dashboardFilterValues,
                    dashboardWhereClauses,
                    dashboardRecordLimit
                )
            )
    );

    return requests.map((request) => {
        // eslint-disable-next-line no-unused-vars
        const { id, ...requestData } = request;
        return {
            ...requestData,
            filters: JSON.stringify(requestData.filters),
        };
    });
}

export function updateMetricData(
    currentMetricData,
    updatedMetricData,
    resetAllData
) {
    const lastUpdated = Date.now();
    const newDataWithTimestamp = updatedMetricData.map((x) => ({
        ...x,
        lastUpdated,
    }));

    if (resetAllData) {
        return newDataWithTimestamp;
    }

    const dataToRemain = currentMetricData.filter(
        (current) =>
            !newDataWithTimestamp.some(
                (updated) =>
                    updated.uniqueRequestString === current.uniqueRequestString
            )
    );

    return [...dataToRemain, ...newDataWithTimestamp];
}

function isMetricDataStale(
    metricData,
    metricRequestIds,
    refreshRateMilliseconds
) {
    const currentTime = Date.now();

    return metricData
        .filter((data) => metricRequestIds.includes(data.uniqueRequestString))
        .some(
            (data) => currentTime - data.lastUpdated > refreshRateMilliseconds
        );
}

export function shouldCallTabData(
    metricData,
    tabWidgets,
    refreshRateMilliseconds
) {
    const existingMetricDataIds = metricData.map((x) => x.uniqueRequestString);

    const tabMetricRequestIds = tabWidgets.reduce(
        (result, tabWidget) => [
            ...result,
            ...tabWidget.widget.metricRequests.map(
                (x) => x.uniqueRequestString
            ),
        ],
        []
    );

    return (
        !existingMetricDataIds.some((x) => tabMetricRequestIds.includes(x)) ||
        isMetricDataStale(
            metricData,
            tabMetricRequestIds,
            refreshRateMilliseconds
        )
    );
}

export function getTimeZoneFilterProperties(timeZoneFiltering, filterValues) {
    if (!timeZoneFiltering) {
        return undefined;
    }

    const { filterKey, type } = timeZoneFiltering;

    const filter = filterValues?.[filterKey];
    if (!filter) {
        return undefined;
    }

    return {
        timeZoneFilterType: type,
        timeZoneFilterValue: filter.values?.[0]?.valueField,
    };
}

export function callData(
    dashboardKey,
    configuration,
    dispatch,
    metricDataUri,
    viewKey,
    widgets,
    lastViewedTimestamps,
    handleError,
    resetAllData
) {
    const metricRequests = flattenAndFormatRequests(
        widgets,
        configuration.timeAndPrecision,
        configuration.filterValues,
        configuration.whereClauses,
        configuration.recordLimit
    );

    setMetricDataLoading(dispatch);

    getMetricData(
        metricDataUri,
        {
            dashboardKey,
            viewKey,
            metricRequests,
            ...getTimeZoneFilterProperties(
                configuration.timeAndPrecision.timeZoneFiltering,
                configuration.filterValues
            ),
        },
        handleError
    ).then((metricData) => {
        if (metricData) {
            setMetricData(
                dispatch,
                metricData,
                lastViewedTimestamps,
                viewKey,
                resetAllData
            );
        }
    });
}

export function viewDataIsExpired(lastViewedTimestamps, viewConfiguration) {
    return (
        !lastViewedTimestamps[viewConfiguration.viewKey] ||
        (viewConfiguration.refreshRateMilliseconds &&
            Date.now() - lastViewedTimestamps[viewConfiguration.viewKey] >
                viewConfiguration.refreshRateMilliseconds)
    );
}
